import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { 
  FaBus, 
  FaCar, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaClock, 
  FaUsers, 
  FaCheckCircle,
  FaStar
} from 'react-icons/fa';

const AnimatedCard: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => {
  const { ref, isVisible } = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-10'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Booking: React.FC = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  
  const [tripType, setTripType] = useState<'oneWay' | 'roundTrip' | 'hourly'>('oneWay');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [vehicles, setVehicles] = useState(1);
  const [hours, setHours] = useState(1);

  const vehiclesList = [
    { id: 1, name: 'GMC Coaster', nameAr: 'جمس كوستر', capacity: 20, image: 'gmc-coaster' },
    { id: 2, name: 'Toyota Hiace', nameAr: 'تويوتا هايس', capacity: 14, image: 'hiace' },
    { id: 3, name: 'Mercedes Sprinter', nameAr: 'مرسيدس سبرينتر', capacity: 18, image: 'sprinter' },
    { id: 4, name: 'BMW', nameAr: 'بي إم دبليو', capacity: 5, image: 'bmw' },
    { id: 5, name: 'Lexus', nameAr: 'لكزس', capacity: 5, image: 'lexus' },
    { id: 6, name: 'Toyota Camry', nameAr: 'تويوتا كامري', capacity: 5, image: 'camry' },
  ];

  const saudiCities = [
    'Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Taif', 'Abha', 'Tabuk',
    'الرياض', 'جدة', 'مكة', 'المدينة', 'الدمام', 'الطائف', 'أبها', 'تبوك'
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle booking search logic
    console.log('Searching...', { tripType, from, to, pickupDate, pickupTime, passengers, vehicles });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 text-white py-16">
        <div className="absolute inset-0 opacity-5 bg-geometric-pattern"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {t('booking.title')}
            </h1>
            <p className="text-xl text-green-50">
              {t('booking.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-12 -mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-light-green-100">
            {/* Trip Type Selection */}
            <div className="flex flex-wrap gap-4 mb-8 justify-center">
              <button
                onClick={() => setTripType('oneWay')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  tripType === 'oneWay'
                    ? 'bg-gradient-to-r from-light-green-500 to-light-green-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('booking.oneWay')}
              </button>
              <button
                onClick={() => setTripType('roundTrip')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  tripType === 'roundTrip'
                    ? 'bg-gradient-to-r from-light-green-500 to-light-green-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('booking.roundTrip')}
              </button>
              <button
                onClick={() => setTripType('hourly')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  tripType === 'hourly'
                    ? 'bg-gradient-to-r from-light-green-500 to-light-green-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('booking.hourly')}
              </button>
            </div>

            {/* Booking Form */}
            <form onSubmit={handleSearch} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* From */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.from')}
                  </label>
                  <div className="relative">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaMapMarkerAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-green-500" size={20} />
                    <input
                      type="text"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      placeholder={language === 'en' ? 'Enter departure city' : 'أدخل مدينة المغادرة'}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none"
                      required
                    />
                  </div>
                </div>

                {/* To */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.to')}
                  </label>
                  <div className="relative">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaMapMarkerAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-green-500" size={20} />
                    <input
                      type="text"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      placeholder={language === 'en' ? 'Enter destination city' : 'أدخل مدينة الوصول'}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pickup Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.pickupTime')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-green-500" size={18} />
                      <input
                        type="date"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div className="relative">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaClock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-green-500" size={18} />
                      <input
                        type="time"
                        value={pickupTime}
                        onChange={(e) => setPickupTime(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Drop-off Date (for round trip) */}
                {tripType === 'roundTrip' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('booking.dropoffTime')}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        {/* @ts-expect-error - react-icons compatibility with React 19 */}
                        <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-green-500" size={18} />
                        <input
                          type="date"
                          value={dropoffDate}
                          onChange={(e) => setDropoffDate(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none"
                          required
                        />
                      </div>
                      <div className="relative">
                        {/* @ts-expect-error - react-icons compatibility with React 19 */}
                        <FaClock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-green-500" size={18} />
                        <input
                          type="time"
                          value={dropoffTime}
                          onChange={(e) => setDropoffTime(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Hours (for hourly) */}
                {tripType === 'hourly' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('booking.hours')}
                    </label>
                    <select
                      value={hours}
                      onChange={(e) => setHours(Number(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                        <option key={h} value={h}>
                          {h} {language === 'en' ? 'Hour' : 'ساعة'} {h > 1 && (language === 'en' ? 's' : '')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Passengers and Vehicles */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.passengerCount')}
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setPassengers(Math.max(1, passengers - 1))}
                      className="w-10 h-10 rounded-lg bg-light-green-100 text-light-green-600 font-bold hover:bg-light-green-200 transition-colors"
                    >
                      -
                    </button>
                    <span className="text-2xl font-bold text-gray-700 w-12 text-center">{passengers}</span>
                    <button
                      type="button"
                      onClick={() => setPassengers(passengers + 1)}
                      className="w-10 h-10 rounded-lg bg-light-green-100 text-light-green-600 font-bold hover:bg-light-green-200 transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.vehicleCount')}
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setVehicles(Math.max(1, vehicles - 1))}
                      className="w-10 h-10 rounded-lg bg-light-green-100 text-light-green-600 font-bold hover:bg-light-green-200 transition-colors"
                    >
                      -
                    </button>
                    <span className="text-2xl font-bold text-gray-700 w-12 text-center">{vehicles}</span>
                    <button
                      type="button"
                      onClick={() => setVehicles(vehicles + 1)}
                      className="w-10 h-10 rounded-lg bg-light-green-100 text-light-green-600 font-bold hover:bg-light-green-200 transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Search Button */}
              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-light-green-500 via-light-green-600 to-saudi-green-600 text-white rounded-lg font-bold text-lg hover:from-light-green-600 hover:via-light-green-700 hover:to-saudi-green-700 transform hover:scale-105 transition-all duration-300 shadow-xl"
              >
                {t('booking.search')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('booking.ourFleet')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('booking.fleetDesc')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vehiclesList.map((vehicle, index) => (
              <AnimatedCard key={vehicle.id} delay={index * 100}>
                <div className="group bg-gradient-to-br from-light-green-50 to-green-50 p-6 rounded-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-2 border-light-green-200 hover:border-light-green-400">
                  <div className="h-48 bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 rounded-lg mb-4 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity"></div>
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaBus className="text-white opacity-50 group-hover:opacity-100 transition-opacity" size={80} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {language === 'en' ? vehicle.name : vehicle.nameAr}
                  </h3>
                  <div className="flex items-center text-gray-600 mb-4">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaUsers className="mr-2" size={16} />
                    <span>{vehicle.capacity} {language === 'en' ? 'Passengers' : 'راكب'}</span>
                  </div>
                  <button className="w-full py-3 bg-gradient-to-r from-light-green-500 to-light-green-600 text-white rounded-lg font-semibold hover:from-light-green-600 hover:to-light-green-700 transition-all duration-300 transform hover:scale-105">
                    {t('booking.bookNow')}
                  </button>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <AnimatedCard delay={0}>
              <div className="bg-white p-8 rounded-lg text-center border-2 border-light-green-100 hover:shadow-xl transition-all duration-300">
                <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-lg flex items-center justify-center mb-4 mx-auto shadow-md">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaCar className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Modern Vehicles' : 'سيارات حديثة'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en' 
                    ? 'We offer the latest vehicles every year to match our clients\' preferences'
                    : 'نقدم كل عام أحدث السيارات لكي نتماشى مع أذواق عملائنا'}
                </p>
              </div>
            </AnimatedCard>

            <AnimatedCard delay={100}>
              <div className="bg-white p-8 rounded-lg text-center border-2 border-light-green-100 hover:shadow-xl transition-all duration-300">
                <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-lg flex items-center justify-center mb-4 mx-auto shadow-md">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaUsers className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Trained Drivers' : 'سائقون مدربون'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en'
                    ? 'Highly committed, safe driving, and good ethics'
                    : 'على أعلى درجة من الالتزام والقيادة الآمنة وذو أخلاق حسنة'}
                </p>
              </div>
            </AnimatedCard>

            <AnimatedCard delay={200}>
              <div className="bg-white p-8 rounded-lg text-center border-2 border-light-green-100 hover:shadow-xl transition-all duration-300">
                <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-lg flex items-center justify-center mb-4 mx-auto shadow-md">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaClock className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Punctual Schedules' : 'مواعيد دقيقة'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en'
                    ? 'Discipline and punctuality are the foundation of any successful business'
                    : 'الانضباط والدقة في المواعيد أساس نجاح أي عمل'}
                </p>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
